import { Link } from 'react-router-dom'

/**
 * The Report Center's top-level segmented toggle: Dashboards | Reports | Queue.
 *
 * Extracted from Reports.jsx so all three destinations render the identical
 * control. Markup and classes are kept byte-identical to the original pill group
 * so nothing shifts visually.
 *
 * IMPORTANT: this group is NOT gated on the Dashboards flag. In Reports.jsx the
 * whole toggle used to be wrapped in `dashboardsEnabled &&` — with Dashboards
 * off there was no toggle at all. The Queue is a peer destination that must stay
 * reachable regardless, so the DASHBOARDS entry is filtered out individually by
 * the caller while the group itself always renders.
 *
 * `tabs`: [{ key, label, to?, onSelect?, count? }] — `to` renders a Link (a real
 * route), `onSelect` renders a button (an in-page ?mode= switch).
 */
const ReportCenterTabs = ({ tabs = [], activeKey, variant = 'page' }) => {
  if (tabs.length < 2) return null

  /*
   * `variant="header"` is the Figma's placement (Row B, `110:9246`): the group
   * sits in the top bar rather than centred above the page. Same control, same
   * roles, same accessible name — only the wrapper's centring/margin and the
   * pill metrics change, because a 40px-tall pill row does not fit an h-16 bar.
   *
   * Kept as a variant rather than a second component so the two can never drift,
   * which is the whole reason this control was extracted from Reports.jsx.
   */
  const inHeader = variant === 'header'

  return (
    <div className={inHeader ? 'flex' : 'flex justify-center mb-6'}>
      <div
        className={`inline-flex bg-gray-100 rounded-full ${inHeader ? 'p-0.5' : 'p-1'}`}
        role="tablist"
        aria-label="Report Center view"
      >
        {tabs.map((t) => {
          const active = t.key === activeKey
          const className = `${
            inHeader ? 'px-3.5 py-1 text-[13px]' : 'px-6 py-2 text-sm'
          } rounded-full font-medium transition-colors inline-flex items-center gap-2 ${
            active ? 'bg-white text-cb-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`
          const inner = (
            <>
              {t.label}
              {t.count > 0 && (
                <span className="inline-flex min-w-[17px] items-center justify-center rounded-full bg-cb-accent-text px-1.5 text-[10px] font-bold leading-[17px] text-white">
                  {t.count}
                </span>
              )}
            </>
          )
          return t.to ? (
            <Link key={t.key} to={t.to} role="tab" aria-selected={active} className={className} data-pendo={`report-tab-${t.key}`}>
              {inner}
            </Link>
          ) : (
            <button key={t.key} type="button" role="tab" aria-selected={active} onClick={t.onSelect} className={className} data-pendo={`report-tab-${t.key}`}>
              {inner}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default ReportCenterTabs
